import * as React from 'react';
import cx from 'classnames';
import { map, toLower } from 'lodash';

import { Alert, Drawer, Form, Input, Button, Modal, Select } from 'antd';

import { ClientFeatureState } from '@frontend/app/types/globalTypes';
import { IClientFeature } from '@frontend/app/hooks';
import { useGetClientFeatureBetas, useDeleteClientFeatureBeta } from '@frontend/app/hooks/clientFeatures';

import { BetaConfigSection } from './BetaConfigSection';
import styles from './EditFeatureDrawer.scss';

const { useMemo, useEffect } = React;
const { Option } = Select;

export interface ICreateFeatureParams {
  id?: number;
  name: string;
  description: string;
  state: ClientFeatureState;
}
interface IProps {
  open: boolean;
  onRequstClose(): void;

  isSubmitting: boolean;
  onSubmit(feature: ICreateFeatureParams): void | Promise<void>;

  feature?: IClientFeature;

  className?: string;
}

interface IEditAppFormProps {
  onSubmit(feature: ICreateFeatureParams): void | Promise<void>;
  isSubmitting: boolean;

  feature?: IClientFeature;
}
const EditFeatureForm: React.FunctionComponent<React.PropsWithChildren<IEditAppFormProps>> = React.memo((props) => {
  const { feature } = props;
  const [form] = Form.useForm();
  const selectedState = Form.useWatch('state', form);

  // A beta only works while the flag is limited/opt-in: once it's globally ENABLED (GA) the flag
  // is on for every client and evaluateFeatureState ignores the per-client opt-in, so the beta's
  // toggle can no longer turn it off. Rather than leave a dead switch, promoting to GA removes the
  // beta — warned up front (below) and confirmed on submit (onFinish).
  const { betas } = useGetClientFeatureBetas();
  const { deleteClientFeatureBeta } = useDeleteClientFeatureBeta();
  const beta = React.useMemo(
    () => (feature?.id ? betas.find((candidate) => candidate.clientFeatureId === feature?.id) : undefined),
    [betas, feature],
  );
  const enablingWithBeta = selectedState === ClientFeatureState.ENABLED && Boolean(beta);
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    },
  };

  useEffect(() => {
    if (feature) {
      form.setFieldsValue({
        id: feature.id,
        name: feature.name,
        description: feature.description,
        state: feature.state,
      });
    } else {
      form.setFieldsValue({
        name: '',
        description: '',
        state: ClientFeatureState.DISABLED,
      });
    }
  }, [form, feature]);

  const onFinish = async (values) => {
    // Promoting a flag that still has a beta to GA leaves a toggle that can't turn the flag off.
    // Confirm, and on "yes" save the flag FIRST, then remove the beta — only after the promotion
    // succeeds — so a failed save never drops the beta while the flag is still limited. (A rejected
    // onOk promise keeps the modal open, so the admin sees the failure.)
    if (values.state === ClientFeatureState.ENABLED && beta) {
      Modal.confirm({
        title: 'Enable this flag and remove its beta?',
        content:
          `Enabling "${feature?.name}" globally turns it on for every client, so its beta on the ` +
          'Beta Features page can no longer be toggled off — it would be a dead switch. Enabling ' +
          'will remove the beta.' +
          (feature?.state === ClientFeatureState.OPEN_BETA
            ? ' Any client that opted out during the open beta will be re-enabled; clients a CSM disabled manually stay off.'
            : ''),
        okText: 'Enable and remove beta',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          await props.onSubmit(values);
          await deleteClientFeatureBeta({ variables: { id: beta.id } });
        },
      });
      return;
    }

    try {
      await props.onSubmit(values);
    } catch (error) {
      console.error('Error saving client feature:', error);
    }
  };
  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name);
  };
  console.log({ feature });

  return (
    <Form form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} className={styles.form} {...formItemLayout}>
      {feature?.id && (
        <Form.Item label="Id" name="id" rules={[{ required: true }]}>
          <Input disabled />
        </Form.Item>
      )}
      <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the feature's name." }]}>
        <Input placeholder="Feature name" />
      </Form.Item>
      <Form.Item
        label="Description"
        name="description"
        rules={[{ required: true, message: "Please enter the feature's description." }]}
      >
        <Input placeholder="Feature description" />
      </Form.Item>
      <Form.Item label="State" name="state">
        <Select>
          {map(ClientFeatureState, (state) => (
            <Option key={state} value={state}>
              {toLower(state)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {enablingWithBeta && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Enabling this flag will remove its beta"
          description="This flag has a beta on the Beta Features page. A GA flag is on for every client, so its beta toggle can no longer turn it off — submitting with State = enabled will remove the beta (you'll be asked to confirm)."
        />
      )}
      <Button type="primary" htmlType="submit" loading={props.isSubmitting} className={styles.submitButton}>
        Submit
      </Button>
    </Form>
  );
});

/**
 * @type {React.FunctionComponent}
 */
export const EditFeatureDrawer: React.FunctionComponent<React.PropsWithChildren<IProps>> = React.memo((props) => {
  const { feature } = props;

  const isNewFeature = useMemo(() => !feature, [feature]);

  return (
    <Drawer
      title={isNewFeature ? 'Create Client Feature' : 'Edit Client Feature'}
      visible={props.open}
      width={600}
      onClose={props.onRequstClose}
      className={cx(styles.EditFeatureDrawer, props.className)}
    >
      <EditFeatureForm onSubmit={props.onSubmit} isSubmitting={props.isSubmitting} feature={props.open && feature} />
      {!isNewFeature && props.open && feature && <BetaConfigSection feature={feature} />}
    </Drawer>
  );
});

EditFeatureDrawer.defaultProps = {
  className: null,
};
